import React, { useState, useEffect, useCallback } from 'react';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  TextField,
  Box,
  Autocomplete,
  FormControlLabel,
  Switch,
} from '@mui/material';
import AddIcon from '@mui/icons-material/Add';
import { referenceAPI } from '../services/api';
import AddReferenceModal from './AddReferenceModal';
import { toArray } from '../utils/apiData';
import { createPrefixFilter } from '../utils/autocompleteFilters';
import { useNotify } from '../context/NotifyContext';
import errorMessage from '../utils/errorMessage';
import useFieldErrors from '../utils/useFieldErrors';

const AddExpenseTypeModal = ({ open, onClose, onSave, onSuccess, apiCall, initialName, initialCategoryName }) => {
  const notify = useNotify();
  const fields = useFieldErrors();
  const [categoryId, setCategoryId] = useState('');
  const [name, setName] = useState('');
  const [active, setActive] = useState(true);
  const [categories, setCategories] = useState([]);
  const [loading, setLoading] = useState(false);
  const [loadingCategories, setLoadingCategories] = useState(false);
  const [openCategoryModal, setOpenCategoryModal] = useState(false);

  // A category can be picked or created from the nested "+"; either satisfies the check.
  useEffect(() => {
    if (categoryId) fields.clear('category');
  }, [categoryId, fields]);

  const loadCategories = useCallback(async () => {
    setLoadingCategories(true);
    try {
      const response = await referenceAPI.getExpenseTypeCategories();
      const categoriesData = toArray(response.data, 'expense type categories');
      
      // Sort categories by sort_order, then by name
      const sorted = [...categoriesData].sort((a, b) => {
        if (a.sort_order !== b.sort_order) {
          return (a.sort_order || 0) - (b.sort_order || 0);
        }
        return a.name.localeCompare(b.name);
      });
      
      setCategories(sorted);
    } catch (error) {
      console.error('Error loading categories:', error);
      notify.error(`Error loading categories: ${errorMessage(error, error.message)}`);
    } finally {
      setLoadingCategories(false);
    }
  }, [notify]);

  useEffect(() => {
    if (open) {
      loadCategories();
      if (initialName) {
        setName(initialName);
      }
    }
  }, [open, initialName, loadCategories]);

  useEffect(() => {
    if (open && initialCategoryName && categories.length > 0 && !categoryId) {
      const match = categories.find(
        c => c.name.toLowerCase() === initialCategoryName.toLowerCase()
      );
      if (match) setCategoryId(String(match.id));
    }
  }, [categories, initialCategoryName, open, categoryId]);

  const handleClose = () => {
    setCategoryId('');
    setName('');
    setActive(true);
    setOpenCategoryModal(false);
    fields.reset();
    onClose();
  };

  const handleSubmit = async () => {
    const valid = fields.validate({
      category: !categoryId && 'Category is required',
      name: !name.trim() && 'Name is required',
    });
    if (!valid) return;

    setLoading(true);
    try {
      const data = {
        category_id: Number(categoryId), // Convert to number for API
        name,
        active,
      };

      // Support both onSave (old style) and apiCall + onSuccess (new style)
      if (onSave) {
        await onSave(data);
        handleClose();
      } else if (apiCall) {
        const response = await apiCall(data);
        if (onSuccess) {
          onSuccess(response.data);
        }
        handleClose();
      }
    } catch (error) {
      console.error('Error saving:', error);
      notify.error(errorMessage(error, 'Error saving expense type'));
    } finally {
      setLoading(false);
    }
  };

  return (
    <>
      <Dialog 
        open={open} 
        onClose={handleClose}
        maxWidth="sm" 
        fullWidth
        sx={{ zIndex: (theme) => theme.zIndex.modal + 2 }}
      >
        <DialogTitle>Add New Expense Type</DialogTitle>
        <DialogContent>
          <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2, mt: 2 }}>
            <Box sx={{ display: 'flex', gap: 1, alignItems: 'flex-start' }}>
              <Autocomplete
                fullWidth
                size="small"
                disableClearable
                loading={loadingCategories}
                disabled={loadingCategories}
                options={categories}
                getOptionLabel={(cat) => cat.name}
                isOptionEqualToValue={(o, v) => String(o.id) === String(v?.id)}
                filterOptions={createPrefixFilter((cat) => cat.name)}
                noOptionsText='No categories available. Click "+ Add" to create one.'
                componentsProps={{
                  popper: { disablePortal: true },
                  paper: { sx: { zIndex: (theme) => theme.zIndex.modal + 3 } },
                }}
                value={categories.find((cat) => String(cat.id) === categoryId) ?? null}
                onChange={(e, newValue) => setCategoryId(newValue ? String(newValue.id) : '')}
                renderInput={(params) => <TextField {...params} label="Category" required {...fields.props('category')} />}
              />
              <Button
                size="small"
                variant="outlined"
                onClick={() => setOpenCategoryModal(true)}
                sx={{ minWidth: 'auto', px: 0.5, height: 40 }}
              >
                <AddIcon fontSize="small" />
              </Button>
            </Box>
            <TextField
                  size="small"
              label="Name"
              value={name}
              onChange={(e) => { setName(e.target.value); fields.clear('name'); }}
              {...fields.props('name')}
              required
              fullWidth
              autoFocus
              placeholder="e.g., Hay, Vaccination, Lessons"
            />
          <FormControlLabel
            control={
              <Switch
                checked={active}
                onChange={(e) => setActive(e.target.checked)}
              />
            }
            label="Active"
          />
          </Box>
        </DialogContent>
        <DialogActions>
          <Button onClick={handleClose} disabled={loading}>
            Cancel
          </Button>
          <Button onClick={handleSubmit} variant="contained" disabled={loading}>
            {loading ? 'Adding...' : 'Add'}
          </Button>
        </DialogActions>
      </Dialog>

      <AddReferenceModal
        open={openCategoryModal}
        onClose={() => setOpenCategoryModal(false)}
        apiCall={referenceAPI.createExpenseTypeCategory}
        onSuccess={async (newCategory) => {
          await loadCategories();
          if (newCategory && newCategory.id) {
            setCategoryId(String(newCategory.id));
          }
        }}
        title="Expense Type Category"
        label="Category Name"
      />
    </>
  );
};

export default AddExpenseTypeModal;
